import {useMemo} from 'react';

/**
 * Unified PDP customize CTA state — loading/disabled until all relevant queries settle,
 * then Notify Me vs Add to Cart.
 *
 * @param {Object} options
 * @param {boolean} [options.useCustomizePath] - Complex / simple+options / freeform customize ATC
 * @param {boolean} [options.freeformIsNotifyMeMode] - Sync freeform cover OOS (no inventory batch)
 * @param {boolean} [options.isProductLoading]
 * @param {boolean} [options.isBasketLoading]
 * @param {boolean} [options.isComplexProduct]
 * @param {boolean} [options.isLoadingSetCards]
 * @param {boolean} [options.isFetchingSetOptionPrices]
 * @param {boolean} [options.customizeNotifyMeMode] - From {@link usePdpInventoryGate}
 * @param {boolean} [options.isInventoryPending] - From {@link usePdpInventoryGate}
 * @returns {{ isCtaPending: boolean, isCtaLoading: boolean, showNotifyMe: boolean }}
 */
export function usePdpCustomizeCtaState({
  useCustomizePath = false,
  freeformIsNotifyMeMode = false,
  isProductLoading = false,
  isBasketLoading = false,
  isComplexProduct = false,
  isLoadingSetCards = false,
  isFetchingSetOptionPrices = false,
  customizeNotifyMeMode = false,
  isInventoryPending = false,
}) {
  const isCtaPending = useMemo(() => {
    if (isProductLoading || isBasketLoading) return true;
    if (!useCustomizePath || freeformIsNotifyMeMode) return false;
    if (isInventoryPending) return true;

    return isComplexProduct && (isLoadingSetCards || isFetchingSetOptionPrices);
  }, [
    isProductLoading,
    isBasketLoading,
    useCustomizePath,
    freeformIsNotifyMeMode,
    isInventoryPending,
    isComplexProduct,
    isLoadingSetCards,
    isFetchingSetOptionPrices
  ]);

  const showNotifyMe = useMemo(() => {
    if (isCtaPending) return false;

    return freeformIsNotifyMeMode || customizeNotifyMeMode;
  }, [isCtaPending, freeformIsNotifyMeMode, customizeNotifyMeMode]);

  const isCtaLoading = isCtaPending || isBasketLoading;

  return {isCtaLoading, isCtaPending, showNotifyMe};
}
